import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store';
import { fetchCollectionsThunk, fetchCreatorsCollectionItemsThunk } from 'slices/collections/thunk';
import { Collection, AccountCollectionItem } from 'types/collection';
import { generateUniqueKey } from 'utils/helpers';
import Loader from 'Components/Common/Loader';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import CreatorCard from 'Components/social-account/SocialAccountCreators/CreatorCard';
import { SocialAccountCreator } from 'types/social-account';
import { DEFAULT_CREATOR_COLLECTION_ID, DEFAULT_POST_COLLECTION_ID } from 'constants/global';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, UncontrolledTooltip } from 'reactstrap';
import BinIcon from 'assets/icons/mono/BinIcon';
import MoveIcon from 'assets/icons/mono/MoveIcon';
import { showCampaignInviteModal, toggleApplicationView, toggleManageCollectionModal } from 'slices/ui/reducer';
import { SocialAccount } from 'types/influencer';
import DeleteCollectionItemModal from './DeleteCollectionItemModal';
import { cleanCreatorsStateAll } from 'slices/socialAccounts/reducer';
import ApplicationView from 'Components/campaigns/applications/ApplicationView';
import eventEmitter from 'utils/eventEmitter';

const AccountCollection = () => {

    const { collectionId } = useParams();
    const collectionsData = useSelector((state: RootState) => state.collections.list);
    const collections = collectionsData?.items ?? [];
    const [loading, setLoading] = useState(false);
    const dispatch: AppDispatch = useDispatch();
    const [filters, setFilters] = useState<any>({ page: 1 });
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [filtersId, setFilterId] = useState(generateUniqueKey(filters, ['page']));
    const applicationView = useSelector((state: RootState) => state.ui.applicationView);
    const creatorsData = useSelector((state: RootState) => state.collections.collections?.[collectionId!]?.data?.items?.filtered_lists?.[filtersId!]);
    const creators = (creatorsData?.items) as AccountCollectionItem[] ?? [];

    const has_more = creatorsData?.has_more ?? true;
    const page = creatorsData?.page ?? 0;
    const [pageLoading, setPageLoading] = useState(false);
    const [selectedForDeletion, setSelectedForDeletion] = useState<SocialAccount | null>(null);
    const [selectedCreatorIds, setSelectedCreatorIds] = useState<string[]>([]);
    const hasSelection = selectedCreatorIds.length > 0;


    const collection: Collection | null = useMemo(() => {
        if (!collectionId) return null;
        if (collectionId === DEFAULT_POST_COLLECTION_ID) return (collectionsData?.default?.post) as Collection ?? null;
        if (collectionId === DEFAULT_CREATOR_COLLECTION_ID) return (collectionsData?.default?.account) as Collection ?? null;
        return collections.find((c) => c.uuid === collectionId) as Collection ?? null;
    }, [collectionId, collections])

    useEffect(() => {
        setSelectedCreatorIds([]);
    }, [collectionId])

    useEffect(() => {
        const clearSelection = () => setSelectedCreatorIds([]);
        eventEmitter.on('campaign-invite-success', clearSelection);
        return () => {
            eventEmitter.off('campaign-invite-success', clearSelection);
        };
    }, [])

    useEffect(() => {
        setLoading(true);
        dispatch(fetchCollectionsThunk({})).finally(() => setLoading(false));
    }, [])

    useEffect(() => {
        if (collectionId && filters) {
            if (filters.page && filters.page === 1) {
                setLoading(true);
                setPageLoading(true);
            }
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            debounceTimeout.current = setTimeout(() => {
                dispatch(fetchCreatorsCollectionItemsThunk({ collectionId, filters })).finally(() => {
                    setLoading(false);
                    setPageLoading(false);
                    setFilterId(generateUniqueKey(filters, ['page']))
                })
            }, 500)
        }
    }, [collectionId, filters])

    const nextPage = () => {
        if (has_more && collectionId && !pageLoading && !loading) {
            const nextPageNumber = page + 1;
            setPageLoading(true);
            dispatch(fetchCreatorsCollectionItemsThunk({ filters: { ...filters, page: nextPageNumber }, collectionId })).finally(() => setPageLoading(false))
        }
    }

    const refetch = async () => {
        if (collectionId) {
            dispatch(fetchCreatorsCollectionItemsThunk({ collectionId, filters, force: true })).finally(() => {
                setLoading(false);
                setPageLoading(false);
                setFilterId(generateUniqueKey(filters, ['page']))
            })
            dispatch(fetchCollectionsThunk({ force: true }));
        }
    }

    const handleToggleCreatorSelect = (creatorId: string) => {
        setSelectedCreatorIds((prev) =>
            prev.includes(creatorId)
                ? prev.filter((id) => id !== creatorId)
                : [...prev, creatorId]
        );
    };

    const handleClearSelection = () => {
        setSelectedCreatorIds([]);
    };

    const handleInviteToCampaign = () => {
        if (selectedCreatorIds.length === 0) return;
        const selectedCreators = creators
            .filter((creator) => selectedCreatorIds.includes(creator.social_account.uuid))
            .map((creator) => ({
                uuid: creator.social_account.uuid,
                name: creator.social_account.name,
                handle: creator.social_account.handle,
                profile_picture_url: creator.social_account.profile_picture_url ?? null,
                platform: creator.social_account.platform,
                followers_count: creator.social_account.followers_count,
            }));
        dispatch(
            showCampaignInviteModal({
                socialAccountIds: selectedCreatorIds,
                creators: selectedCreators,
            })
        );
    };

    if (loading) return <Loader />

    return (
        <>
            <div>
                <div className="collection-header">
                    <div className="collection-header-left">
                        <Link to="/collection" className="collection-back-link" aria-label="Back to collections">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M5.5 12.0254L18.5 12.0254" stroke="#222222" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M11.5469 18.049L5.49687 12.025L11.5469 6" stroke="#222222" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Link>
                        {
                            collectionId === DEFAULT_POST_COLLECTION_ID && (
                                <div className="d-flex align-items-center gap-1">
                                    <h5 className="collection-title">All Posts</h5>
                                    <span className="collection-items-count">{collection?.count} Creator{(collection?.count ?? 0) > 1 ? 's' : ''}</span>

                                </div>
                            )
                        }
                        {
                            collectionId === DEFAULT_CREATOR_COLLECTION_ID && (
                                <div className="d-flex align-items-center gap-1">
                                    <h5 className="collection-title">All Creators</h5>
                                    <span className="collection-items-count">{(collection?.count ?? 0)} Creator{(collection?.count ?? 0) > 1 ? 's' : ''}</span>

                                </div>
                            )
                        }
                        {
                            collectionId !== DEFAULT_POST_COLLECTION_ID && collectionId !== DEFAULT_CREATOR_COLLECTION_ID && collection && (
                                <div className="d-flex align-items-center gap-1">
                                    <h5 className="collection-title">{collection.name}</h5>
                                    <span className="collection-items-count">{collection.count} Creator{collection.count > 1 ? 's' : ''}</span>
                                </div>
                            )
                        }
                        {hasSelection && (
                            <div className="collection-selection">
                                <span className="collection-selected-count">
                                    {selectedCreatorIds.length} selected
                                </span>
                                <button
                                    type="button"
                                    className="collection-clear-selection"
                                    onClick={handleClearSelection}
                                >
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>
                        <div className="collection-header-actions">
                            <span
                                id="collection-invite-to-campaign-btn"
                                className="d-inline-block"
                            >
                                <button
                                    disabled={!hasSelection}
                                    type="button"
                                    className="btn btn-primary collection-invite-btn"
                                    onClick={handleInviteToCampaign}
                                    style={{ pointerEvents: hasSelection ? 'auto' : 'none' }}
                                >
                                    <i className="ri-mail-send-line" />
                                    Invite to Campaign
                                </button>
                            </span>
                            {!hasSelection && (
                                <UncontrolledTooltip
                                    placement="top"
                                    target="collection-invite-to-campaign-btn"
                                    fade={false}
                                    autohide={false}
                                >
                                    {creators.length === 0
                                        ? "No creators in this collection. Add creators first."
                                        : "Select at least one creator to invite them to a campaign."}
                                </UncontrolledTooltip>
                            )}
                        </div>
                </div>
                <div className="collection-items">
                    <InfiniteScroll
                        dataLength={creators.length}
                        next={nextPage}
                        className="creator-grid"
                        hasMore={has_more}
                        loader={<></>}
                    >
                        {creators.map((creator, i) => (
                            <div key={creator.social_account.uuid ?? i}>
                                <CreatorCard creator={creator as SocialAccountCreator}
                                    onClick={(socialAccountId) => dispatch(toggleApplicationView({socialAccountId}))}
                                    isSelected={selectedCreatorIds.includes(creator.social_account.uuid)}
                                    onToggleSelect={handleToggleCreatorSelect}
                                    alwaysVisibleActions={
                                        <UncontrolledDropdown direction="down">
                                            <DropdownToggle className='unset-all post-preview-action-btn'>
                                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <rect width="32" height="32" rx="16" fill="black" fillOpacity="0.7" />
                                                    <path d="M10.1667 17.6654C11.0871 17.6654 11.8333 16.9192 11.8333 15.9987C11.8333 15.0782 11.0871 14.332 10.1667 14.332C9.24619 14.332 8.5 15.0782 8.5 15.9987C8.5 16.9192 9.24619 17.6654 10.1667 17.6654Z" stroke="white" strokeWidth="1.25" />
                                                    <path d="M16.0026 17.6654C16.9231 17.6654 17.6693 16.9192 17.6693 15.9987C17.6693 15.0782 16.9231 14.332 16.0026 14.332C15.0821 14.332 14.3359 15.0782 14.3359 15.9987C14.3359 16.9192 15.0821 17.6654 16.0026 17.6654Z" stroke="white" strokeWidth="1.25" />
                                                    <path d="M21.8307 17.6654C22.7512 17.6654 23.4974 16.9192 23.4974 15.9987C23.4974 15.0782 22.7512 14.332 21.8307 14.332C20.9103 14.332 20.1641 15.0782 20.1641 15.9987C20.1641 16.9192 20.9103 17.6654 21.8307 17.6654Z" stroke="white" strokeWidth="1.25" />
                                                </svg>
                                            </DropdownToggle>
                                            <DropdownMenu className='mt-1 post-preview-action-menu' container='body'>
                                                <DropdownItem className='post-preview-action-menu-item' onClick={() => setSelectedForDeletion(creator.social_account)}>
                                                    <BinIcon /> Remove from collection
                                                </DropdownItem>
                                                <DropdownItem className='post-preview-action-menu-item' onClick={() => dispatch(toggleManageCollectionModal({ post: null, creator: creator.social_account, itemType: 'account', addMode: false, currentCollectionId: collectionId }))}>
                                                    <MoveIcon /> Copy/Move to another collection
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </UncontrolledDropdown>
                                    }
                                />
                            </div>
                        ))}
                        {
                            (loading || pageLoading) && Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton height={'270px'} className='w-100 d-block' />
                            ))
                        }

                        {
                            !(loading || pageLoading) && creators.length === 0 &&
                            <div className="d-flex w-100 justify-content-center align-items-center my-5">
                                <div className="length-zero my-5">
                                    <h3>Currently there is no creator in your list</h3>
                                </div>
                            </div>
                        }
                    </InfiniteScroll>
                </div>
            </div>
            <DeleteCollectionItemModal
                isOpen={!!selectedForDeletion}
                onClose={() => setSelectedForDeletion(null)}
                creator={selectedForDeletion}
                onDelete={async () => {
                    dispatch(cleanCreatorsStateAll());
                    await refetch();
                }}
                itemType='account'
            />
            <ApplicationView
                isOpen={!!applicationView}
                socialAccountId={applicationView?.socialAccountId}
                toggleOffcanvas={() => dispatch(toggleApplicationView(null))}
                showPermalink={false}
            />
        </>
    )
}

export default AccountCollection